import { FC, ReactNode, useMemo } from 'react';

import { BasicFacetTermV2, BasicFacetWithTermsV2 } from '@ravago/shared/page-data/models/elastic';
import { useTranslations } from '@ravago/shared/translations/client';

import { FacetContentV2 } from '../../../models/facet-group.model';

import { FacetTermV2, RangeChangeEventV2 } from './facet-term-v2';

interface Props {
   searchValue: string;
   isParentActive?: boolean;
   facetContent: FacetContentV2;
   activeTerm?: BasicFacetTermV2;
   activeNestedFacetForThisParent?: BasicFacetWithTermsV2;
   onCheckedChange: (newValue: boolean) => void;
   onRangeChange: (event: RangeChangeEventV2) => void;
   renderChild: (isParentActive: boolean) => ReactNode | undefined;
}

export const FacetTermItemV2: FC<Props> = ({
   searchValue,
   isParentActive,
   facetContent,
   activeTerm,
   activeNestedFacetForThisParent,
   onCheckedChange,
   onRangeChange,
   renderChild
}) => {
   const translate = useTranslations();

   const areAllChildrenActive = useMemo(() => {
      if (!activeNestedFacetForThisParent || !activeNestedFacetForThisParent?.terms?.length || !facetContent.nestedFacetGroups) return false;
      const termSum = activeNestedFacetForThisParent?.terms?.filter((term) => term.parentKey === facetContent.key || !term.parentKey)?.length;
      const facetSum = facetContent.nestedFacetGroups.map((nestedFacet) => nestedFacet.facets.length).reduce((acc, curr) => acc + curr, 0);
      return termSum === facetSum;
   }, [facetContent.nestedFacetGroups, activeNestedFacetForThisParent]);

   const active = useMemo(() => {
      if (isParentActive) return true;
      if (areAllChildrenActive) return true;

      return !!activeTerm;
   }, [isParentActive, activeTerm, areAllChildrenActive]);

   const indeterminate = useMemo(
      () =>
         !areAllChildrenActive &&
         !!activeNestedFacetForThisParent &&
         activeNestedFacetForThisParent?.terms?.filter((term) => term.parentKey === facetContent.key || !term.parentKey)?.length > 0,
      [activeNestedFacetForThisParent, areAllChildrenActive]
   );

   const range =
      activeTerm && typeof activeTerm?.range?.min === 'number' && typeof activeTerm?.range?.max === 'number'
         ? { min: activeTerm.range.min, max: activeTerm.range.max }
         : undefined;

   return (
      <FacetTermV2
         key={facetContent.key}
         label={translate(facetContent.label)}
         highlightValue={searchValue}
         facet={facetContent}
         checked={active}
         indeterminate={indeterminate}
         range={range}
         disabled={facetContent.count === 0}
         onCheckedChange={() => onCheckedChange(!active)}
         onRangeChange={onRangeChange}
      >
         {renderChild(active)}
      </FacetTermV2>
   );
};
